import {
  captureException,
  captureMessage,
} from '@sentry/nextjs';

// Track events in New Relic
const EVENTS = {
  // Disallow Password Check Failure
  DISALLOW_PASSWORD_CHECK_FAILURE: 'DisallowPasswordCheckFailure',
  // Disallow Password Check Success
  DISALLOW_PASSWORD_CHECK_SUCCESS: 'DisallowPasswordCheckSuccess',
  // Failed Masquerade Spoof
  FAILED_SPOOF: 'FailedSpoof',
  // Failed refresh
  FAILED_TOKEN_REFRESH: 'FailedTokenRefresh',
  // Failed refresh out of attempts
  FAILED_TOKEN_REFRESH_RETRIES_EXHAUSTED: 'FailedTokenRefreshRetriesExhausted',
  // Failed refresh unexpected result.  This is the symptom that the token
  // was not updated after edit profile happened
  FAILED_TOKEN_REFRESH_UNEXPECTED: 'FailedTokenRefreshUnexpected',
  // Failed Token Validate
  FAILED_TOKEN_VALIDATE: 'FailedTokenValidate',
  // User was unable to login for any reason including bad pass
  LOGIN_FAILURE: 'LoginFailure',
  // Error validating token received on login
  LOGIN_FAILURE_INVALID_TOKEN_STORE: 'LoginFailureInvalidTokenStore',
  // Failed Mono Connection post login
  LOGIN_FAILURE_MONO: 'LoginFailureMono',
  // Login Failure type we don't know how to handle
  LOGIN_FAILURE_UNKNOWN_ERROR: 'LoginFailureUnknown',
  // Could not send reset token to mono send to user.
  MONO_PASSWORD_RESET_SEND_FAILURE: 'MonoPasswordResetSendFailure',
  // could not create a new user in Mono
  MONO_USER_CREATION_FAILURE: 'MonoUserCreationFailure',
  // could not look up user in mono
  MONO_USER_LOOKUP_FAILURE: 'MonoLookupUserFailure',
  // could not update user in mono
  MONO_USER_UPDATE_FAILURE: 'MonoUpdateUserFailure',
  // Could not choose the password for the user
  OKTA_ADMIN_PASSWORD_CHOOSE_FAILURE: 'OktaAdminPasswordChooseFailure',
  // Could not expire password
  OKTA_EXPIRE_PASSWORD_FAILURE: 'OktaExpirePasswordFailure',
  // Okta error creating user
  OKTA_FAILED_CREATE_USER: 'OktaFailCreateUser',
  // Okta error deactivating user
  OKTA_FAILED_DEACTIVATE_USER: 'OktaFailDeactivateUser',
  // Okta PKCE Verification Failure
  OKTA_ID_TOKEN_PKCE_VERIFICATION_FAILURE: 'OktaIdTokenFailed',
  // Could get a user from okta, but couldn't get reset token
  OKTA_PASSWORD_RESET_TOKEN_AVAILABLE: 'PasswordResetTokenUnavailable',
  // Okta Resource Owner token failure
  OKTA_RESOURCE_TOKEN_FAILURE: 'OktaResourceTokenFailure',
  // Could not create a user in Okta
  OKTA_USER_CREATION_FAILURE: 'OktaUserCreationFailure',
  // Transfer Cart Failure
  TRANSFER_CART_FAILURE: 'TransferCartFailure',
};

const TRACES = {
  EDIT_PROFILE_SUBMIT: 'EditProfileSubmit',
  FACEBOOK_LOGIN_SUBMIT: 'FacebookLoginSubmit',
  LOGIN_SUBMIT: 'LoginSubmit',
  REGISTER_SUBMIT: 'RegisterSubmit',
  RESET_SUBMIT: 'ResetSubmit',
};

/**
 * Record a custom event.
 * @param customEvent
 * @param attributes
 */
const sendCustomEvent = (customEvent:string, attributes:{ [keys: string]: boolean | number | string })  => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('Would send Event in prod: ', customEvent, 'with args:', attributes);
  }
  captureMessage(customEvent, {
    extra: attributes,
  });

  const merged = {
    ...attributes,
    ...{
      // Is this necessary or does newrelic track it?
      date_now: Date.now(),
    },
  };

  if (typeof window === 'undefined') {
    const newrelic = require('newrelic');

    newrelic.recordCustomEvent(customEvent, merged);
  } else {
    // @ts-ignore-next-line
    if (window.newrelic) {
      // @ts-ignore-next-line
      window.newrelic.addPageAction(customEvent, merged);
    } else {
      // eslint-disable-next-line no-console
      console.log('Newrelic not enabled on client');
    }
  }
};

const sendCustomError = (error: Error) => {
  captureException(error);
};

// Sentry used to have this function, but deprecated it.
// It's useful, so here's a slimmed down version
const fromHttpCode = (httpStatusCode: number) => {
  if (httpStatusCode === 200) {
    return 'ok';
  } else if (httpStatusCode >= 500 && httpStatusCode < 600) {
    return 'internal_error';
  }

  return 'unknown_error';
};

export {
  fromHttpCode,
  EVENTS,
  sendCustomEvent,
  sendCustomError,
  TRACES,
};
